// Shows and hides pills in a row with motion (Block Company Listing's Location row: picking a
// continent swaps the other continents for its countries). The new hidden states apply at once -
// the filter logic never waits for an animation:
//
// - Pills that leave, and those that stay, just change at once - no motion.
// - Pills that appear (those `animated` accepts - the rest just show) slide up from below into
//   place, cropped to their own box so they rise out of nothing, staggered.
// - The row's height follows, so a row that wraps onto more or fewer lines (desktop) moves what's
//   below it smoothly instead of jumping.
//
// Instant with reduced motion.

// UNVERIFIED: no Figma for this motion - duration, easing and the stagger are a first proposal,
// to be checked with the designer.
const REVEAL_MS = 300;
const STAGGER_MS = 40;
// Pills past this many share the last delay, so a long country list doesn't trail on.
const MAX_STAGGER_STEPS = 10;
const EASING = "ease-out";

// Jumps a row's swap still running to its end state, before the next one measures.
const settleRunning = new WeakMap<HTMLElement, () => void>();

function canAnimate(row: HTMLElement): boolean {
    return typeof row.animate === "function" && !window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

// hiddenStates: each pill's new hidden state. Resolves once the motion has finished (at once
// without it) - e.g. for a scroll row to re-check whether it still overflows.
export function swapPillVisibility(
    row: HTMLElement,
    hiddenStates: ReadonlyMap<HTMLElement, boolean>,
    animated: (pill: HTMLElement) => boolean = () => true,
): Promise<void> {
    settleRunning.get(row)?.();

    const changing = Array.from(hiddenStates).filter(([pill, hidden]) => pill.hidden !== hidden);
    if (changing.length === 0) {
        return Promise.resolve();
    }
    if (!canAnimate(row)) {
        changing.forEach(([pill, hidden]) => {
            pill.hidden = hidden;
        });
        return Promise.resolve();
    }

    const fromHeight = row.getBoundingClientRect().height;
    changing.forEach(([pill, hidden]) => {
        pill.hidden = hidden;
    });
    const toHeight = row.getBoundingClientRect().height;
    const entering = changing.filter(([pill, hidden]) => !hidden && animated(pill)).map(([pill]) => pill);

    // The clip moves with the pill, so it's shifted the other way (a negative top inset) to stay
    // on the pill's final box: only the part that has risen into it shows.
    const animations: Animation[] = entering.map((pill, index) => pill.animate(
        [
            { transform: "translateY(100%)", clipPath: "inset(-100% 0 100% 0)" },
            { transform: "none", clipPath: "inset(0)" },
        ],
        {
            duration: REVEAL_MS,
            delay: Math.min(index, MAX_STAGGER_STEPS) * STAGGER_MS,
            easing: EASING,
            // Hidden during its delay too, not shown and then blinked out when it starts.
            fill: "backwards",
        },
    ));
    if (Math.abs(toHeight - fromHeight) >= 1) {
        animations.push(row.animate(
            [{ height: `${fromHeight}px` }, { height: `${toHeight}px` }],
            { duration: REVEAL_MS, easing: EASING },
        ));
    }

    // Once, whichever comes first: the animations finishing, or the next swap starting.
    let settled = false;
    const settle = (): void => {
        if (settled) {
            return;
        }
        settled = true;
        settleRunning.delete(row);
        animations.forEach((animation) => animation.cancel());
    };
    settleRunning.set(row, settle);
    return Promise.all(animations.map((animation) => animation.finished)).then(settle, settle);
}
